import { Outlet } from 'react-router-dom';
import PATH_PAGE from '@/router/path';
import useParamWorkspaceId from '@/hook/useParamWorkspaceId';
import useDemoPath from '@/hook/useDemoPath';
import { useMemo, useEffect } from 'react';
import useConsoleLayoutConfig from '@/hook/useConsoleLayoutConfig';
import { useServerlessStore } from '@/store/serverless';
import { useGetServerlessToken } from '@/service/serverless-gpu/hook';
import { useGPUInstanceStore } from '@/store/gpu-instance';
import { useProductSectionTracking } from '@/hooks/useProductTracking';

const GPUInstanceLayout = () => {
  useProductSectionTracking('gpu_instance');
  console.log('GPUInstanceLayout rendering');
  const { orgId, workspaceId } = useParamWorkspaceId();
  console.log('GPUInstanceLayout params:', { orgId, workspaceId });
  const { setToken } = useGPUInstanceStore();
  const { loading } = useGPUInstanceStore();
  const { withDemoPrefix } = useDemoPath();

  // Reuse serverless token since they share the same authentication
  const { data, isLoading } = useGetServerlessToken(
    {
      workspace_id: workspaceId!,
    },
    {
      retry: (failureCount, error: any) => {
        if (error?.response?.data?.details === 'Not found serverless token') {
          return false;
        }
        return failureCount < 3;
      },
      staleTime: 1000 * 60 * 5,
    }
  );

  useEffect(() => {
    if (!isLoading) {
      setToken(data?.token || null);
    }
  }, [data?.token, isLoading, setToken]);

  const navItems = [
    {
      href: withDemoPrefix(PATH_PAGE.dashboard.gpuInstance.overview(orgId, workspaceId)),
      label: 'Overview',
      strictMatch: true,
    },
    {
      href: withDemoPrefix(PATH_PAGE.dashboard.gpuInstance.instances(orgId, workspaceId)),
      label: 'Instances',
      strictMatch: true,
    },
    {
      href: withDemoPrefix(PATH_PAGE.dashboard.gpuInstance.create(orgId, workspaceId)),
      label: 'Create Instance',
      strictMatch: true,
    },
    {
      href: withDemoPrefix(PATH_PAGE.dashboard.gpuInstance.volume(orgId, workspaceId)),
      label: 'Volume',
      strictMatch: true,
    },
    {
      href: withDemoPrefix(PATH_PAGE.dashboard.gpuInstance.pricing(orgId, workspaceId)),
      label: 'Pricing',
      strictMatch: true,
    },
  ];

  const navbarConfig = useMemo(
    () => ({
      title: 'GPU Instance',
      navbar: navItems,
    }),
    [workspaceId]
  );

  useConsoleLayoutConfig({
    navConfig: navbarConfig,
  });

  if (loading) {
    return null;
  }

  return <Outlet />;
};

export default GPUInstanceLayout;
